import { useEffect, useState, useRef } from 'react';
import get from 'lodash/get';
import { useAtomValue as useStateSegmentValue, useSetAtom } from 'jotai';
import { useResetAtom } from 'jotai/utils';
import {
  RiArrowDownSLine,
  RiDownload2Line,
  RiFilter3Line,
} from '@remixicon/react';
import styles from './KnowledgeCenter.module.css';
import Tabs, { TabsVariant } from '../../molecules/Tabs';
import Tab, { TabVariant } from '../../atoms/Tab';
import EmptyTab from '../../molecules/BrandsInformationTabs/EmptyTab';
import BrandDetailsTab from '../../molecules/BrandsInformationTabs/BrandDetailsTab';
import IndicationsTab from '../../molecules/BrandsInformationTabs/IndicationsTab';
import ClaimsTab from '../../molecules/BrandsInformationTabs/ClaimsTab';
import SegmentsTab from '../../molecules/BrandsInformationTabs/SegmentsTab';
import ImagesTab from '../../molecules/BrandsInformationTabs/ImagesTab';
import BrandDetailsFilter from '../BrandDetailsFilter';
import {
  fetchBrandDetails,
  fetchSegmentDetails,
} from '../../../store/markettingContentGeneration/actions/majorProcessActions';
import {
  knowledgeCenterStateSegment,
  Claim,
  Image,
  Pagination,
  ApiPayloadUsed,
} from '../../../store/markettingContentGeneration/stateSegments/brandsInformationStateSegment';
import { claimMetadataStateSegment } from '../../../store/markettingContentGeneration/stateSegments/claimMetadataStateSegment';
import { fetchClaimDetails } from '../../../store/markettingContentGeneration/actions/majorProcessActions/fetchClaimDetails';
import { fetchClaimMetadata } from '../../../store/markettingContentGeneration/actions/microProcessActions';
import {
  INCLUDES_PARAMS,
  BrandPageTabName,
  ClaimMetadataIncludes,
} from '../../../constants/common';
import { translate } from '../../../localization';
import { fetchImages } from '../../../store/markettingContentGeneration/actions/majorProcessActions/fetchImages';
import IconButton from '../../molecules/IconButton';
import {
  downloadAllImages,
  DownloadImagesResponse,
} from '../../../store/markettingContentGeneration/actions/majorProcessActions/downloadAllImages';
import SnackBar, {
  SnackBarPosition,
  SnackBarVariant,
} from '../../atoms/SnackBar';
import { useSnackBar } from '../../../hooks/snackBar/useSnackBar';
import { useWindowWidth } from '../../../hooks/useWindowWidth';
import { httpClient, HTTPClientResponse } from '../../../utils/httpClient';
import { useFeatureFlags } from '../../../hooks/useFeatureFlags';
import CapsuleDropdown from '../../molecules/CapsuleDropdown';

export default function KnowledgeCenter() {
  const windowWidth = useWindowWidth();

  // Get feature flags
  const { isAemAssetIntegrationEnabled } = useFeatureFlags();

  const {
    showSnackBar,
    isSnackBarVisible,
    hideSnackBar,
    notificationText,
    snackBarTheme,
  } = useSnackBar();

  interface Tab {
    tabName: BrandPageTabName;
    panel: JSX.Element;
  }

  const [activeTab, setActiveTab] = useState(BrandPageTabName.BRAND_DETAILS);
  const [downloading, setDownloading] = useState(false);
  const [filtersApplied, setFiltersApplied] = useState(false);
  const [isNonOnboardedBrand, setIsNonOnboardedBrand] = useState(false);
  const [selectedLanguages, setSelectedLanguages] = useState<string[]>([]);
  const [selectedAboveMarketTeams, setSelectedAboveMarketTeams] = useState<
    string[]
  >([]);

  // Track if we're initializing dropdowns to prevent infinite loops
  const isInitializingDropdowns = useRef(false);

  const brandInformation = useStateSegmentValue(knowledgeCenterStateSegment);
  const claimMetadata = useStateSegmentValue(claimMetadataStateSegment);
  const resetBrandInformation = useResetAtom(knowledgeCenterStateSegment);
  const setBrandInformation = useSetAtom(knowledgeCenterStateSegment);

  // Initialize dropdown values from API payload when it changes
  useEffect(() => {
    isInitializingDropdowns.current = true;

    if (brandInformation.apiPayloadUsed.languages) {
      setSelectedLanguages(brandInformation.apiPayloadUsed.languages);
    } else {
      setSelectedLanguages([]);
    }

    if (brandInformation.apiPayloadUsed.aboveMarketTeams) {
      setSelectedAboveMarketTeams(
        brandInformation.apiPayloadUsed.aboveMarketTeams.filter(
          (team): team is string => team !== undefined
        )
      );
    } else {
      setSelectedAboveMarketTeams([]);
    }

    // Reset flag after state updates are complete
    setTimeout(() => {
      isInitializingDropdowns.current = false;
    }, 0);
  }, [
    brandInformation.apiPayloadUsed.brand,
    brandInformation.apiPayloadUsed.country,
    brandInformation.apiPayloadUsed.indication,
    brandInformation.apiPayloadUsed.targetAudience,
    brandInformation.apiPayloadUsed.topics,
  ]);

  // Prepare dropdown options from claim metadata
  const getLanguageOptions = () => {
    const metadata = claimMetadata.data.filterDetails[0];
    if (metadata?.languages?.length > 0) {
      return metadata.languages.map(lang => ({
        id: lang.code,
        name: lang.code,
        label: lang.name,
        // Disable the single option when there's only one item in the list for multiselect
        isDisabled: metadata.languages.length === 1,
      }));
    }
    return [];
  };

  const getAboveMarketTeamOptions = () => {
    const metadata = claimMetadata.data.filterDetails[0];
    if (metadata?.aboveMarketTeams?.length > 0) {
      return metadata.aboveMarketTeams.map(team => ({
        id: team,
        name: team,
        label: team,
        // Disable the single option when there's only one item in the list for multiselect
        isDisabled: metadata.aboveMarketTeams.length === 1,
      }));
    }
    return [];
  };

  const languageOptions = getLanguageOptions();
  const aboveMarketTeamOptions = getAboveMarketTeamOptions();

  // Auto-select single options when they become available
  useEffect(() => {
    // Auto-select single language option
    if (languageOptions.length === 1 && languageOptions[0].name) {
      // If there's only one option, always ensure it's selected and clear any other selections
      if (
        selectedLanguages.length !== 1 ||
        selectedLanguages[0] !== languageOptions[0].name
      ) {
        setSelectedLanguages([languageOptions[0].name]);
      }
    }

    // Auto-select single above market team option
    if (aboveMarketTeamOptions.length === 1 && aboveMarketTeamOptions[0].name) {
      // If there's only one option, always ensure it's selected and clear any other selections
      if (
        selectedAboveMarketTeams.length !== 1 ||
        selectedAboveMarketTeams[0] !== aboveMarketTeamOptions[0].name
      ) {
        setSelectedAboveMarketTeams([aboveMarketTeamOptions[0].name]);
      }
    }
  }, [
    languageOptions,
    aboveMarketTeamOptions,
    selectedLanguages,
    selectedAboveMarketTeams,
  ]);

  // Fetch claim metadata when filters are applied and Claims tab becomes active
  const fetchClaimMetadataIfNeeded = async () => {
    const { brand, country, indication, targetAudience, topics } =
      brandInformation.apiPayloadUsed;

    await fetchClaimMetadata({
      brand,
      country,
      indication,
      targetAudience,
      topics,
      includes: [
        ClaimMetadataIncludes.LANGUAGE,
        ClaimMetadataIncludes.ABOVE_MARKET_TEAM,
      ],
    });
  };

  const tabs: Tab[] = [
    { tabName: BrandPageTabName.BRAND_DETAILS, panel: <BrandDetailsTab /> },
    { tabName: BrandPageTabName.INDICATIONS, panel: <IndicationsTab /> },
    {
      tabName: BrandPageTabName.IMAGES,
      panel: <ImagesTab />,
    },
    {
      tabName: BrandPageTabName.SEGMENTS,
      panel: <SegmentsTab />,
    },
    {
      tabName: BrandPageTabName.CLAIMS,
      panel: <ClaimsTab isNonOnboardedBrand={isNonOnboardedBrand} />,
    },
  ];

  const {
    brandDetails: { isLoading },
  } = brandInformation;

  // Handle filter changes when a tab is already active
  const handleFiltersApplied = (
    newApiPayload: ApiPayloadUsed,
    isNonOnboarded: boolean
  ) => {
    // Reset dropdown selections when new filters are applied
    // unless the new payload already contains these values
    if (!newApiPayload.languages) {
      setSelectedLanguages([]);
    }
    if (!newApiPayload.aboveMarketTeams) {
      setSelectedAboveMarketTeams([]);
    }

    // Clear existing data when filters change but preserve the API payload
    setBrandInformation(prev => ({
      ...prev,
      apiPayloadUsed: newApiPayload,
      brandDetails: {
        data: {
          id: '',
          name: '',
          description: '',
          additionalBrandDetails: [],
          supportingInfo: [],
          indications: [],
        },
        isLoading: false,
        hasNoData: false,
        errorMessage: '',
      },
      segmentDetails: {
        data: {
          segments: [],
        },
        isLoading: false,
        hasNoData: false,
        errorMessage: '',
      },
      claimDetails: {
        data: {
          claimsPagination: {
            total: 0,
            page: 0,
            totalPages: 0,
            nextPage: null,
            previousPage: null,
          },
          claims: [],
        },
        isLoading: false,
        hasNoData: false,
        errorMessage: '',
      },
      images: {
        data: {
          imagesPagination: {
            total: 0,
            page: 0,
            totalPages: 0,
            nextPage: null,
            previousPage: null,
            nextPageToken: '',
          },
          images: [],
        },
        isLoading: false,
        hasNoData: false,
        errorMessage: '',
      },
      isLoading: false,
      errorMessage: '',
      errorFetchSegment: false,
    }));

    // Update non-onboarded state
    setIsNonOnboardedBrand(isNonOnboarded);

    // If non-onboarded brand, don't call APIs for Claims, Segments, Images
    if (
      !isNonOnboarded ||
      (isNonOnboarded && activeTab !== BrandPageTabName.CLAIMS)
    ) {
      switch (activeTab) {
        case BrandPageTabName.CLAIMS: {
          const claimsPayload = {
            ...newApiPayload,
            languages:
              selectedLanguages.length > 0 ? selectedLanguages : undefined,
            aboveMarketTeams:
              selectedAboveMarketTeams.length > 0
                ? selectedAboveMarketTeams
                : undefined,
          };

          fetchClaimDetails(claimsPayload);
          // Also fetch metadata for dropdowns
          fetchClaimMetadata({
            brand: newApiPayload.brand,
            country: newApiPayload.country,
            indication: newApiPayload.indication,
            targetAudience: newApiPayload.targetAudience,
            topics: newApiPayload.topics,
            includes: [
              ClaimMetadataIncludes.LANGUAGE,
              ClaimMetadataIncludes.ABOVE_MARKET_TEAM,
            ],
          });
          break;
        }
        case BrandPageTabName.SEGMENTS:
          fetchSegmentDetails(newApiPayload);
          break;
        case BrandPageTabName.IMAGES:
          fetchImages({
            ...newApiPayload,
            page: 1,
            pageSize: 20,
            aemAssetIntegrationEnabled: isAemAssetIntegrationEnabled,
          });
          break;
        case BrandPageTabName.BRAND_DETAILS:
        case BrandPageTabName.INDICATIONS: {
          const brandsApiPayload = {
            ...newApiPayload,
            includes: INCLUDES_PARAMS,
          };
          fetchBrandDetails(brandsApiPayload);
          break;
        }
        default:
          break;
      }
    }

    setFiltersApplied(true);
  };

  const handleTabClick = (tab: Tab) => {
    if (isNonOnboardedBrand && tab.tabName === BrandPageTabName.CLAIMS) {
      setActiveTab(tab.tabName);
      return;
    }

    // Check if apiPayloadUsed is empty and exit early if so
    if (
      !brandInformation.apiPayloadUsed.brand ||
      !brandInformation.apiPayloadUsed.country
    ) {
      setActiveTab(tab.tabName);
      return;
    }

    if (
      filtersApplied &&
      tab.tabName === BrandPageTabName.SEGMENTS &&
      !brandInformation.segmentDetails.data.segments[0]?.audience
    ) {
      fetchSegmentDetails(brandInformation.apiPayloadUsed);
    }

    if (
      filtersApplied &&
      (tab.tabName === BrandPageTabName.BRAND_DETAILS ||
        tab.tabName === BrandPageTabName.INDICATIONS) &&
      !brandInformation?.brandDetails.data?.id
    ) {
      const brandsApiPayload = {
        ...brandInformation.apiPayloadUsed,
        includes: INCLUDES_PARAMS,
      };
      fetchBrandDetails(brandsApiPayload);
    }

    if (
      filtersApplied &&
      tab.tabName === 'Claims' &&
      !brandInformation.claimDetails.data.claims[0]?.id
    ) {
      const claimsPayload = {
        ...brandInformation.apiPayloadUsed,
        languages: selectedLanguages.length > 0 ? selectedLanguages : undefined,
        aboveMarketTeams:
          selectedAboveMarketTeams.length > 0
            ? selectedAboveMarketTeams
            : undefined,
      };

      fetchClaimDetails(claimsPayload);
      // Also fetch claim metadata for dropdowns
      fetchClaimMetadataIfNeeded();
    }

    if (
      filtersApplied &&
      tab.tabName === BrandPageTabName.IMAGES &&
      (!brandInformation.images?.data?.images ||
        brandInformation.images.data.images === null ||
        brandInformation.images.data.images.length === 0)
    ) {
      fetchImages({
        ...brandInformation.apiPayloadUsed,
        page: 1,
        pageSize: 20,
        aemAssetIntegrationEnabled: isAemAssetIntegrationEnabled,
      });
    }

    setActiveTab(tab.tabName);
  };

  useEffect(() => {
    return () => {
      resetBrandInformation();
    };
  }, []);

  // Fetch claim details with updated filters when dropdown selections change
  useEffect(() => {
    // Don't trigger API calls if we're just initializing dropdowns
    if (isInitializingDropdowns.current) {
      return;
    }

    const updatedPayload = {
      ...brandInformation.apiPayloadUsed,
      languages: selectedLanguages.length > 0 ? selectedLanguages : undefined,
      aboveMarketTeams:
        selectedAboveMarketTeams.length > 0
          ? selectedAboveMarketTeams
          : undefined,
      claimsPage: 1, // Reset to first page when filters change
    };

    fetchClaimDetails(updatedPayload);
  }, [selectedLanguages, selectedAboveMarketTeams]);

  const images = get(brandInformation, 'images.data.images', [] as Image[]);

  const claimsData = get(
    brandInformation,
    'claimDetails.data.claims',
    [] as Claim[]
  );

  const claimsPagination = get(
    brandInformation,
    'claimDetails.data.claimsPagination',
    {} as Pagination
  );

  const claimsTotalRecordsCount = get(claimsPagination, 'total', 0);

  const onDownloadAllImages = async (): Promise<void> => {
    setDownloading(true);
    const { apiPayloadUsed } = brandInformation;

    try {
      let downloadResponse: DownloadImagesResponse;

      if (isAemAssetIntegrationEnabled) {
        // For AEM asset integration, poll until status is COMPLETED or FAILED
        let pollCount = 0;
        let currentEventId: string | undefined = undefined;
        const maxPolls = 60; // Maximum number of polls (e.g., 5 minutes with 5-second intervals)

        do {
          downloadResponse = await downloadAllImages({
            ...apiPayloadUsed,
            eventId: currentEventId, // Pass eventId for subsequent requests
          });

          const status = downloadResponse.data?.imagesDownload?.[0]?.status;

          // Store eventId from response for subsequent polling requests
          if (downloadResponse.data?.eventId) {
            currentEventId = downloadResponse.data.eventId;
          }

          if (status === 'COMPLETED') {
            break; // Exit polling loop
          }

          if (status === 'FAILED') {
            throw new Error('Image download preparation failed');
          }

          // If status is INITIATED or IN_PROGRESS, wait before next poll
          if (status === 'INITIATED' || status === 'IN_PROGRESS') {
            await new Promise(resolve => setTimeout(resolve, 3000)); // Wait 3 seconds
            pollCount++;
          } else {
            // Unknown status, proceed with download
            break;
          }
        } while (pollCount < maxPolls);

        // Check if we exceeded max polls
        if (pollCount >= maxPolls) {
          throw new Error(
            'Download preparation timed out. Please try again later.'
          );
        }
      } else {
        // For non-AEM asset integration, use current flow
        downloadResponse = await downloadAllImages({
          ...apiPayloadUsed,
        });
      }

      const { data, success } = downloadResponse;

      if (data && success) {
        const { imagesDownload } = data || {};

        const response = (await httpClient.get<ArrayBuffer>(
          imagesDownload[0].url,
          {},
          {},
          'arraybuffer',
          {},
          true
        )) as HTTPClientResponse;
        if (!response.data) throw new Error('Failed to fetch the ZIP file');

        const blob = new Blob([response.data], {
          type: response.headers?.['content-type'] || 'application/zip',
        });
        const url = window.URL.createObjectURL(blob);

        const a = document.createElement('a');
        a.href = url;
        a.download = `${apiPayloadUsed.brand}-images.zip`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);

        window.URL.revokeObjectURL(url);

        setDownloading(false);
        showSnackBar(
          'Images downloaded successfully.',
          SnackBarVariant.SUCCESS
        );
      } else {
        throw new Error('Failed to prepare download');
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error downloading the ZIP file:', error);
      setDownloading(false);
      showSnackBar(
        'Images downloading failed. Please try again after sometime.',
        SnackBarVariant.ERROR
      );
    }
  };

  return (
    <>
      <div className={styles['KnowledgeCenter-container']}>
        <div className={styles.KnowledgeCenter__content}>
          <div className={styles['KnowledgeCenter__content--inner']}>
            <div className={styles.KnowledgeCenter__header}>
              <BrandDetailsFilter onFiltersApplied={handleFiltersApplied} />

              {filtersApplied && !isLoading && (
                <div className={styles['KnowledgeCenter__section-header']}>
                  <Tabs variant={TabsVariant.COMPACT}>
                    {tabs.map(tab => {
                      return (
                        <Tab
                          variant={TabVariant.COMPACT}
                          key={tab.tabName}
                          onClick={() => handleTabClick(tab)}
                          isActive={activeTab === tab.tabName}
                        >
                          {tab.tabName}
                        </Tab>
                      );
                    })}
                  </Tabs>

                  {activeTab === BrandPageTabName.CLAIMS &&
                    claimsData?.length > 0 &&
                    filtersApplied && (
                      <div
                        className={
                          styles['KnowledgeCenter__claims-filter-wrapper']
                        }
                      >
                        {windowWidth > 1280 &&
                          aboveMarketTeamOptions.length > 0 &&
                          brandInformation.apiPayloadUsed.country?.toLowerCase() ===
                            'global' && (
                            <CapsuleDropdown
                              name='above-market-team-filter'
                              list={aboveMarketTeamOptions}
                              value={selectedAboveMarketTeams}
                              handleSelection={value =>
                                setSelectedAboveMarketTeams(value as string[])
                              }
                              label='Above Market Team'
                              iconBefore={
                                <RiFilter3Line size={15} color='#0000C9' />
                              }
                              iconAfter={
                                <RiArrowDownSLine size={15} color='#0000C9' />
                              }
                              multiSelect
                            />
                          )}
                        {windowWidth > 1280 && (
                          <CapsuleDropdown
                            name='language-filter'
                            list={languageOptions}
                            value={selectedLanguages}
                            handleSelection={value =>
                              setSelectedLanguages(value as string[])
                            }
                            label={translate('language')}
                            iconBefore={
                              <RiFilter3Line size={15} color='#0000C9' />
                            }
                            iconAfter={
                              <RiArrowDownSLine size={15} color='#0000C9' />
                            }
                            multiSelect
                          />
                        )}
                        <div
                          className={
                            styles['KnowledgeCenter__table-records-count']
                          }
                        >
                          {claimsTotalRecordsCount} {translate('common_claims')}
                        </div>
                      </div>
                    )}

                  {activeTab === BrandPageTabName.CLAIMS &&
                    !claimsData?.length &&
                    filtersApplied && (
                      <div
                        className={
                          styles['KnowledgeCenter__claims-filter-wrapper']
                        }
                      >
                        <div
                          className={
                            styles['KnowledgeCenter__table-records-count']
                          }
                        >
                          ({translate('displaying')} {claimsData?.length}{' '}
                          {translate('results')})
                        </div>
                      </div>
                    )}

                  {activeTab === BrandPageTabName.IMAGES &&
                    images?.length > 0 &&
                    filtersApplied && (
                      <div
                        className={
                          styles['KnowledgeCenter__table-records-count']
                        }
                      >
                        <IconButton
                          onClick={onDownloadAllImages}
                          className={styles.downloadBtn}
                          ariaLabel='Download All Images'
                          type='button'
                          disabled={downloading}
                        >
                          <RiDownload2Line color='#0000C9' />
                          {downloading
                            ? translate('downloading')
                            : translate('download_all')}
                        </IconButton>
                      </div>
                    )}
                </div>
              )}

              {activeTab === BrandPageTabName.CLAIMS &&
                claimsData?.length > 0 &&
                filtersApplied &&
                windowWidth <= 1280 && (
                  <div
                    className={styles['KnowledgeCenter__claims-filter-wrapper']}
                  >
                    {aboveMarketTeamOptions.length > 0 &&
                      brandInformation.apiPayloadUsed.country?.toLowerCase() ===
                        'global' && (
                        <CapsuleDropdown
                          name='above-market-team-filter'
                          list={aboveMarketTeamOptions}
                          value={selectedAboveMarketTeams}
                          handleSelection={value =>
                            setSelectedAboveMarketTeams(value as string[])
                          }
                          label='Above Market Team'
                          iconBefore={
                            <RiFilter3Line size={15} color='#0000C9' />
                          }
                          iconAfter={
                            <RiArrowDownSLine size={15} color='#0000C9' />
                          }
                          multiSelect
                        />
                      )}
                    <CapsuleDropdown
                      name='language-filter'
                      list={languageOptions}
                      value={selectedLanguages}
                      handleSelection={value =>
                        setSelectedLanguages(value as string[])
                      }
                      label={translate('language')}
                      iconBefore={<RiFilter3Line size={15} color='#0000C9' />}
                      iconAfter={<RiArrowDownSLine size={15} color='#0000C9' />}
                      multiSelect
                    />
                  </div>
                )}
            </div>

            <div className={styles.KnowledgeCenter__tab_content}>
              {filtersApplied ? (
                tabs.find(tab => tab.tabName === activeTab)?.panel
              ) : (
                <EmptyTab />
              )}
            </div>
          </div>
        </div>
      </div>

      {isSnackBarVisible && (
        <SnackBar
          theme={snackBarTheme}
          notificationText={notificationText}
          hideSnackBar={hideSnackBar}
          position={SnackBarPosition.BOTTOM_CENTER}
        />
      )}
    </>
  );
}
